import { SmallAddIcon } from "@chakra-ui/icons";
import { Button, HStack, Link, MenuItem, VStack } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import {
  BanksBulkUpsertMutationBody,
  getBanksListQueryKey,
  useBanksBulkUpsert,
  useBanksListSuspense,
} from "gen/endpoints/bank/bank";
import { BankReadData } from "gen/model/bankReadData";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import React from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Restricted } from "shared/components/access/Restricted";
import {
  ContentBlock,
  ContentBlockActions,
  ContentBlockBody,
  ContentBlockGroup,
  ContentBlockHeader,
  ContentBlockMenu,
  ContentBlockTitle,
} from "shared/components/content/ContentBlock";
import Descriptions from "shared/components/descriptions/Descriptions";
import EmptyState from "shared/components/empty/EmptyState";
import { FormDrawer } from "shared/components/form/FormDrawer";
import { FormFieldPrefix } from "shared/components/form/FormFieldPrefix";
import { FormFieldset } from "shared/components/form/FormFieldset";
import { FormHiddenInput } from "shared/components/form/FormHiddenInput";
import { FormTextInput } from "shared/components/input/text/TextInput";
import { invalidateQueryKeys } from "shared/utils/QueryUtils";
import { useBoolean } from "usehooks-ts";

export const EntityEditBankInfoBlock: React.FC<{
  entityId: string;
}> = ({ entityId }) => {
  const { t } = useTranslation(["entity", "entity", "common"]);

  const { data } = useBanksListSuspense({ entity_id: entityId });

  const {
    value: isDrawerOpen,
    setTrue: openDrawer,
    setFalse: closeDrawer,
  } = useBoolean();

  return (
    <>
      <ContentBlockGroup
        allowMultiple={true}
        defaultIndex={data.length > 0 ? [0] : undefined}
      >
        <ContentBlock mb="24px">
          <ContentBlockHeader>
            <ContentBlockTitle>{t("entity:bankInfo.title")}</ContentBlockTitle>
            <ContentBlockActions>
              <Restricted permission={PermissionTypeEnum.config}>
                <ContentBlockMenu>
                  <MenuItem onClick={openDrawer}>{t("common:edit")}</MenuItem>
                </ContentBlockMenu>
              </Restricted>
            </ContentBlockActions>
          </ContentBlockHeader>
          {data.length ? (
            <ContentBlockBody variant="descriptions">
              <VStack alignItems="stretch" spacing="16px">
                {data.map((bank) => (
                  <BankDescriptions key={bank.id} bank={bank} />
                ))}
              </VStack>
            </ContentBlockBody>
          ) : (
            <ContentBlockBody>
              <EmptyState
                title={t("entity:bankInfo.empty.title")}
                my={0}
                py={0}
              >
                <Restricted permission={PermissionTypeEnum.config}>
                  <Button colorScheme="secondary" onClick={openDrawer}>
                    {t("common:configure")}
                  </Button>
                </Restricted>
              </EmptyState>
            </ContentBlockBody>
          )}
        </ContentBlock>
      </ContentBlockGroup>
      {isDrawerOpen && (
        <BankInfoEditDrawer entityId={entityId} onCloseComplete={closeDrawer} />
      )}
    </>
  );
};

const BankDescriptions: React.FC<{
  bank: BankReadData;
}> = ({ bank }) => {
  const { t } = useTranslation(["entity"]);
  return (
    <Descriptions>
      <Descriptions.Item
        label={t("entity:bankInfo.bankName")}
        isSensitive={true}
      >
        {bank.name}
      </Descriptions.Item>
      <Descriptions.Item
        label={t("entity:bankInfo.abaRoutingNumber")}
        isSensitive={true}
      >
        {bank.aba}
      </Descriptions.Item>
      <Descriptions.Item
        label={t("entity:bankInfo.accountNumber")}
        isSensitive={true}
      >
        {bank.account_number}
      </Descriptions.Item>
      <Descriptions.Item label={t("entity:bankInfo.accountName")}>
        {bank.account_name}
      </Descriptions.Item>
      <Descriptions.Item label={t("entity:bankInfo.furtherCreditTo")}>
        {bank.for_further_credit_to}
      </Descriptions.Item>
      <Descriptions.Item label={t("entity:bankInfo.ddaNumber")}>
        {bank.dda}
      </Descriptions.Item>
    </Descriptions>
  );
};

const BankInfoEditDrawer: React.FC<{
  entityId: string;
  onCloseComplete: () => void;
}> = ({ entityId, onCloseComplete }) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation(["entity"]);

  const { data } = useBanksListSuspense({ entity_id: entityId });

  const { mutateAsync } = useBanksBulkUpsert();

  const form = useForm<BanksBulkUpsertMutationBody>({
    defaultValues: {
      upsert_data_list: data.length ? data : [{}],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "upsert_data_list",
  });

  const onSubmit = async (data: BanksBulkUpsertMutationBody) => {
    await mutateAsync({ data });
  };

  const onSuccess = async () => {
    await invalidateQueryKeys(
      queryClient,
      getBanksListQueryKey({ entity_id: entityId }),
    );
  };

  return (
    <FormDrawer
      title={t("entity:bankInfo.edit.title")}
      formProps={{
        form,
        onSubmit,
        onSuccess,
      }}
      onCloseComplete={onCloseComplete}
    >
      {fields.map((_, index) => (
        <BankInfoFormInputs
          key={index}
          entityId={entityId}
          index={index}
          remove={() => remove(index)}
        />
      ))}
      <Button
        colorScheme="secondary"
        size="sm"
        iconSpacing="2px"
        leftIcon={<SmallAddIcon />}
        onClick={() =>
          append({
            entity_id: entityId,
            name: "",
            aba: "",
            account_number: "",
            account_name: "",
            for_further_credit_to: "",
            dda: "",
          })
        }
      >
        {t("entity:bankInfo.edit.add")}
      </Button>
    </FormDrawer>
  );
};

const BankInfoFormInputs: React.FC<{
  entityId: string;
  index: number;
  remove: () => void;
}> = ({ entityId, index, remove }) => {
  const { t } = useTranslation(["entity"]);
  return (
    <FormFieldPrefix prefix={`upsert_data_list.${index}`}>
      <FormFieldset border="1px dashed" borderColor="gray.400">
        <FormHiddenInput name="entity_id" value={entityId} />
        <FormTextInput
          name="name"
          title={t("entity:bankInfo.bankName")}
          autoFocus={index === 0}
        />
        <FormTextInput
          name="aba"
          title={t("entity:bankInfo.abaRoutingNumber")}
        />
        <FormTextInput
          name="account_number"
          title={t("entity:bankInfo.accountNumber")}
        />
        <FormTextInput
          name="account_name"
          title={t("entity:bankInfo.accountName")}
        />
        <FormTextInput
          name="for_further_credit_to"
          title={t("entity:bankInfo.furtherCreditTo")}
        />
        <FormTextInput name="dda" title={t("entity:bankInfo.ddaNumber")} />
        <HStack justifyContent="flex-end">
          <Link size="sm" colorScheme="gray" onClick={() => remove()}>
            {t("entity:bankInfo.edit.remove")}
          </Link>
        </HStack>
      </FormFieldset>
    </FormFieldPrefix>
  );
};
